"use client";

import type { JSX } from "react";
import { motion, type Variants } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { CONSULTATION_URL } from "@/lib/consultation-links";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const headingVariants: Variants = {
  hidden: { opacity: 0, filter: "blur(8px)" },
  visible: {
    opacity: 1,
    filter: "blur(0px)",
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  },
};

const cardsContainerVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15, delayChildren: 0.1 } },
};

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 48 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.65, 0, 0.35, 1] },
  },
};

const pricingPlans = [
  {
    name: "Starter Shield",
    description: "Perfect for testing new markets",
    prices: [
      { label: "Own Name", amount: "$1000" },
      // { label: "With Nominee", amount: "€2,200" },
    ],
    note: "Get Started in 7 Days",
    highlight: "HK Company + Airwallex Only",
    featureHeading: "Incorporation",
      features: ["Fast Full company registration","Payoneer with VIP WhatsApp support, just 1% fee instead of 2%, plus secure and reliable global payments.", "Airwallex banking account","Airwallex Account Application","Many more bank accounts opening like Currenxie, PingPong, Worldfirst"],
 
  },
  {
    name: "Fortress Package",
    description: "Complete protection for serious sellers",
    prices: [
      { label: "Own Name", amount: "$1,400" },
      // { label: "With Nominee", amount: "€3,500" },
    ],
    highlight: "Everything You Need to Scale",
    featureHeading: "What's Included",
    popular: true,
    features: [
      "Fast HK Company Registration",
      "PayPal with VIP Support (6% dispute tolerance)",
      "Payoneer VIP Account and 5+ more banks",
      "Airwallex Account",
      
      "Access to 9+ PayPal Reps",
      "Unlimited PayPal Reactivations",
      "Stripe",
    ],
  },
  {
    name: "Titanium Protection",
    description: "Maximum security for high-volume sellers",
    prices: [
      { label: "Own Name", amount: "$3000" },
      // { label: "With Nominee", amount: "€2,200" },
    ],
    note: "Get Started in 7 Days",
    featureHeading: "Incorporation",
    features: [
      "Everything in Fortress Package PLUS",
      "OceanPayments Integration (2.5% dispute tolerance)",
      "Priority Support Queue",
      "Dedicated Account Manager",
    ],
  },
];

export const CompanySetupPricingSection = (): JSX.Element => {
  return (
    <section
      id="pricing"
      className="flex w-full scroll-mt-24 flex-col items-center gap-16 bg-black px-4 pt-20 text-[#ffffff] sm:px-6"
      aria-labelledby="company-setup-pricing-heading"
    >
      <header className="flex w-full max-w-[937px] flex-col items-center">
         <Badge className="animate-revolving-border rounded-[30px] border-0 bg-[#6a6a6a80] px-4 py-2 [font-family:'Suisse_Intl_Trial-Medium',Helvetica] text-sm font-medium leading-[23.4px] tracking-[0] text-white backdrop-blur-[3.5px]">
           Pricing
          </Badge>
        <div className="mt-6 flex w-full flex-col items-center gap-4">
          <motion.h2
            id="company-setup-pricing-heading"
            className="[text-shadow:0px_4px_24px_#0000000d] [font-family:'Suisse_Works_Trial-Regular',Helvetica] text-center text-[34px] font-normal leading-tight tracking-[-1.92px] text-white sm:text-5xl sm:leading-[64px]"
            variants={headingVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.6 }}
          >
            Choose Your Hong Kong
            <br />
            Company Setup
          </motion.h2>
          <motion.p
            className="[font-family:'Suisse_Intl_Trial-Regular',Helvetica] text-center text-lg font-normal leading-6 text-[#fefefe] sm:text-xl"
            variants={headingVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.6 }}
          >
      The current pricing page lists these three
            packages.
          </motion.p>
        </div>
      </header>
      <motion.div
        className="grid w-full max-w-[1224px] grid-cols-1 gap-6 lg:grid-cols-3 lg:items-end"
        variants={cardsContainerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        {pricingPlans.map((plan) => (
          <motion.div key={plan.name} variants={cardVariants}>
          <Card
            className={`relative min-w-0 rounded-3xl border-[#30363d] bg-[#0e1116] p-3 text-card-foreground shadow-none sm:p-5 ${
              plan.popular ? "pt-10" : ""
            }`}
          >
            {plan.popular && (
              <Badge className="absolute animate-revolving-border -top-10 left-1/2 h-auto -translate-x-1/2 rotate-[5deg] whitespace-nowrap rounded-[30px] border-0 bg-[#6a6a6a80] px-4 py-2 [font-family:'Suisse_Intl_Trial-Medium',Helvetica] text-sm font-medium leading-[23.4px] text-white backdrop-blur-[3.5px]">
                MOST CHOSEN
              </Badge>
            )}

            <CardContent className="flex flex-col gap-6 p-0">
              <div className="flex flex-col items-center justify-center gap-6 rounded-[33.86px] border-[2.08px] border-[#ffffff4c] bg-[#ffffff33] px-3 py-6 backdrop-blur-[24.92px] sm:px-[24.92px] sm:py-10">
                <div className="w-full">
                  <h3 className="[font-family:'Inter',Helvetica] text-base font-bold leading-tight text-white sm:text-[22px]">
                    {plan.name}
                  </h3>
                  <p className="mt-1 [font-family:'Inter',Helvetica] text-[11px] font-normal leading-[18.2px] text-[#8b949e] sm:text-[13px]">
                    {plan.description}
                  </p>
                </div>
                <div className="w-full">
                  <div className="flex gap-2">
                    {plan.prices.map((price) => (
                      <div
                        key={price.label}
                        className="min-w-0 flex-1 rounded-2xl bg-[#ffffff1a] p-2 sm:p-4"
                      >
                        <p className="[font-family:'Inter',Helvetica] text-[10px] font-normal leading-3 text-white sm:text-base sm:leading-4">
                          {price.label}
                        </p>
                        <p className="mt-1 [font-family:'Inter',Helvetica] text-base font-normal tracking-[-0.38px] text-white sm:text-[27.5px] sm:leading-7">
                          {price.amount}
                        </p>
                      </div>
                    ))}
                  </div>
                  {plan.popular ? (
                    <p className="mt-2 [font-family:'Inter',Helvetica] text-[10px] leading-[18.2px] sm:text-[13px]">
                      <span className="font-bold text-[#6fa6ff]">87%</span>
                      <span className="font-medium text-white">
                        {" "}
                        of Clients Choose This
                      </span>
                    </p>
                  ) : (
                    <p className="mt-2 [font-family:'Inter',Helvetica] text-[10px] font-medium leading-[18.2px] text-white sm:text-[13px]">
                      {plan.note}
                    </p>
                  )}
                </div>
                <Button
                  asChild
                  className="h-10 w-full rounded-3xl bg-[#0b3bff] px-3 [font-family:'Inter',Helvetica] text-xs font-bold text-white hover:bg-[#0b3bff]/90 sm:h-12 sm:text-sm"
                >
                  <a href={CONSULTATION_URL} target="_blank" rel="noopener noreferrer">
                    Get this plan
                  </a>
                </Button>
              </div>
              {plan.highlight && (
                <p className="bg-[radial-gradient(50%_50%_at_50%_50%,rgba(255,255,255,1)_0%,rgba(112,166,255,1)_100%)] bg-clip-text [font-family:'Inter',Helvetica] text-sm font-medium italic leading-5 text-transparent [-webkit-text-fill-color:transparent] sm:text-xl sm:leading-7">
                  {plan.highlight}
                </p>
              )}

              <div>
                <h4 className="[font-family:'Inter',Helvetica] text-xs font-bold leading-normal text-white sm:text-sm">
                  {plan.featureHeading}
                </h4>
                <ul className="mt-3 flex flex-col gap-3">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2.5">
                      <img
                        className="mt-px h-4 w-4 shrink-0"
                        alt=""
                        aria-hidden="true"
                        src="/hong-kong-company-formation-v2/check.svg"
                      />
                      <span className="[font-family:'Inter',Helvetica] text-[10px] font-normal leading-[14px] text-[#8b949e] sm:text-[13px] sm:leading-[18.2px]">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
};
